import React from "react";

import "./charts-kit.css";

export type T_ChartSeriesHeaderEntry = {
  /** Dot fill matching the series color in the chart. */
  color?: string;
  /** Optional trend readout (e.g. "-17.91%") rendered beside the value. */
  delta?: { positive: boolean; text: string };
  /** Ring-style marker (expected-style) instead of a filled dot. */
  hollow?: boolean;
  /** Series display name. */
  name: string;
  /** Pre-formatted display value: latest by default, scanned while scanning. */
  value: string;
};

export type T_ChartSeriesHeaderProps = {
  entries: T_ChartSeriesHeaderEntry[];
  /** Muted context line: the scanned point's label, or the latest label. */
  label?: string | null;
};

/**
 * Shared legend-with-values header shown above every chart plot — web mirror
 * of the mobile ChartSeriesHeader. Each series renders its color marker,
 * name, and current value; while the user scans, callers swap in the scanned
 * point's values and label, so the header itself is the readout and no popup
 * ever covers the plot.
 */
export function ChartSeriesHeader({
  entries,
  label,
}: T_ChartSeriesHeaderProps) {
  return (
    <div className="ck-header">
      <div className="ck-header-row">
        {entries.map((entry, index) => (
          <div className="ck-header-item" key={`${entry.name}-${index}`}>
            {entry.hollow ? (
              <span className="ck-header-dot ck-header-dot--hollow" />
            ) : entry.color ? (
              <span
                className="ck-header-dot"
                style={{ backgroundColor: entry.color }}
              />
            ) : null}
            <span className="ck-header-name">{entry.name}</span>
            <span className="ck-header-value">{entry.value}</span>
            {entry.delta ? (
              <span
                className={
                  entry.delta.positive
                    ? "ck-header-delta ck-header-delta--positive"
                    : "ck-header-delta ck-header-delta--negative"
                }
              >
                {entry.delta.text}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {label ? <span className="ck-header-label">{label}</span> : null}
    </div>
  );
}
